import type { Crop } from 'react-image-crop';

/**
 * Creates a cropped, circular Blob from an image URL based on crop parameters.
 *
 * @param crop The crop parameters (x, y, width, height in pixels).
 * @param imgUrl The URL of the source image.
 * @param imgZoom The zoom percentage applied to the image.
 * @param containerHeight The height of the container displaying the image cropper.
 * @returns A Promise resolving to the cropped image Blob (PNG format).
 */
export function createCroppedLogoBlob(
  crop: Crop,
  imgUrl: string,
  options?: { imgZoom: number; containerHeight: number }
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!crop || !imgUrl) {
      return reject(new Error('Crop details or image URL are missing.'));
    }

    const image = new Image();
    image.onload = async () => {
      try {
        const { naturalWidth, naturalHeight } = image;
        const imgZoom = options?.imgZoom ?? 100;
        const containerHeight = options?.containerHeight ?? naturalHeight;

        // Calculate the base scale to fit the image within the container height
        const baseScale = Math.min(containerHeight / naturalWidth, containerHeight / naturalHeight);

        const zoomFactor = imgZoom / 100;
        const effectiveScale = baseScale * zoomFactor;

        // Calculate the dimensions of the image as displayed in the cropper
        const displayedWidth = naturalWidth * effectiveScale;
        const displayedHeight = naturalHeight * effectiveScale;

        // Calculate the origin (top-left corner) of the displayed image within the container
        const displayOriginX = (containerHeight - displayedWidth) / 2;
        const displayOriginY = (containerHeight - displayedHeight) / 2;

        // Calculate the source region in the original image corresponding to the crop area
        // The crop coordinates (crop.x, crop.y) are relative to the displayed image's container.
        // We need to translate these to coordinates relative to the original image.
        const sourceX = (crop.x - displayOriginX) / effectiveScale;
        const sourceY = (crop.y - displayOriginY) / effectiveScale;
        // The crop width/height is relative to the displayed image, scale it back to get source dimensions
        const sourceWidth = crop.width / effectiveScale;
        const sourceHeight = crop.height / effectiveScale;

        // Create an offscreen canvas with the dimensions of the final cropped area
        const offscreen = new OffscreenCanvas(crop.width, crop.height);
        const ctx = offscreen.getContext('2d');
        if (ctx == null || !(ctx instanceof OffscreenCanvasRenderingContext2D)) {
          throw new Error('Failed to get 2D context from OffscreenCanvas');
        }

        ctx.beginPath();
        const radius = Math.min(offscreen.width, offscreen.height) / 2;
        ctx.arc(offscreen.width / 2, offscreen.height / 2, radius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(
          image,
          sourceX, // Source X position in original image
          sourceY, // Source Y position in original image
          sourceWidth, // Source width in original image
          sourceHeight, // Source height in original image
          0, // Destination X position on canvas
          0, // Destination Y position on canvas
          offscreen.width, // Destination width on canvas
          offscreen.height // Destination height on canvas
        );

        const blob = await offscreen.convertToBlob({
          type: 'image/png',
        });

        if (!blob) {
          throw new Error('Failed to convert canvas to Blob');
        }

        resolve(blob);
      } catch (error) {
        console.error('Error processing image:', error);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };
    image.onerror = (error) => {
      console.error('Error loading image:', error);
      reject(new Error('Failed to load image.'));
    };
    image.src = imgUrl;
  });
}
